if (!customElements.get('product-addon')) {
  customElements.define(
    'product-addon',
    /**
     * Represents a custom web component for managing product add-ons.
     * This class extends the native HTMLElement and is responsible for handling user interactions,
     * validating input data, and managing the addition of a product add-on to a shopping cart.
     */
    class ProductAddon extends HTMLElement {
      constructor() {
        super();
    
        this.submitButton.addEventListener('click', this.onSubmitHandler.bind(this));
      }
    
      /**
       * Retrieves the value of the first 'select' element within the current context.
       * If no 'select' element exists, returns null.
       *
       * @return {string|null} The value of the 'select' element or null if not found.
       */
      get addon() {
        return this.querySelector('select')?.value || null;
      }

      get bundleProduct() {
        return this.querySelector('[name="properties[_bundle_product]"]')?.value || null;
      }

      get addonPrice() {
        return this.querySelector('select option:checked')?.dataset.price || null;
      }

      get feeProduct() {
        return this.querySelector('select option:checked')?.dataset.feeProduct || null;
      }

      /**
       * Retrieves the value of the element with the class 'extra-info-input' within the current context.
       * If the element is not found or the value is undefined, returns null.
       *
       * @return {string|null} The value of the 'extra-info-input' element, or null if the element is not present or has no value.
       */
      get extraInfo() {
        return this.querySelector('.extra-info-input')?.value || null;
      }

      /**
       * Retrieves the property name associated with the extra information input element.
       *
       * @return {string} The processed property name of the extra information input element.
       *                  If the property name is not found, returns 'Addon extra information' by default.
       */
      get extraInfoPropertyName() {
        let name = this.querySelector('.extra-info-input')?.getAttribute('name');

        if (!name) return 'Addon extra information';

        return name.replace('properties[', '').replace(']', '');
      }

      /**
       * Retrieves the value of the element with the attribute name "id".
       *
       * @return {string|null} The value of the input element with the name "id", or null if the element is not found.
       */
      get productId() {
        return document.querySelector('[name="id"]')?.value || null;
      }

      /**
       * Retrieves the value of the "quantity" input field from the document.
       * If the input field is not found or has no value, it defaults to 1.
       *
       * @return {string|number} The value of the quantity input field or 1 if not defined.
       */
      get quantity() {
        return document.querySelector('[name="quantity"]')?.value || 1;
      }
    
      /**
       * Retrieves the submit button element from the DOM.
       *
       * @return {Element|null} The submit button element if it exists, otherwise null.
       */
      get submitButton() {
        return document.querySelector('.product-form__submit');
      }
    
      /**
       * Retrieves the cart drawer element from the DOM.
       *
       * @return {Element|null} The cart drawer element if it exists, otherwise null.
       */
      get cartDrawer() {
        return document.querySelector('cart-drawer');
      }
    
      /**
       * Handles the submission of a product form, adds an addon child item and addon extra info to line item properties.
       *
       * @param {Event} event - The event object triggered by the form submission.
       * @return {void} This method does not return a value.
       */
      onSubmitHandler(event) {
        if(!this.addon || !this.productId) return;

        event.preventDefault();
        event.stopPropagation();

        const bundleUniqueId = `bundle-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        const parentProperties = {
          '_bundle_product': this.bundleProduct,
          '_unique_id': bundleUniqueId,
        };
        const childProperties = {
          '_bundle_product': this.bundleProduct,
          '_unique_id': bundleUniqueId,
        };

        if (this.extraInfo) {
          parentProperties[this.extraInfoPropertyName] = this.extraInfo;
        }

        const data = {
          items: [
            {
              id: this.productId,
              quantity: this.quantity,
              properties: parentProperties
            },
            {
              id: this.addon,
              quantity: this.quantity,
              properties: childProperties
            }
          ]
      };

        if (this.feeProduct) {
          data.items.push({
            id: this.feeProduct,
            quantity: this.quantity,
            properties: { ...childProperties }
          });
        }

        if (document.body.classList.contains('template-cart') || theme.settings.cartType === 'page') {
          theme.utils.postLink2(theme.routes.cart_add_url, {
            parameters: {
              ...data
            }
          });
          return;
        }
        
        event.preventDefault();
        if (this.submitButton.hasAttribute('aria-disabled')) return;
        this.activeElement = event.submitter || event.currentTarget;
    
        this.handleErrorMessage();
    
        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));
        
        const body = JSON.stringify({
          ...data,
          sections: sectionsToBundle,
          sections_url: window.location.pathname
        });
    
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.setAttribute('aria-busy', 'true');
    
        fetch(`${theme.routes.cart_add_url}`, { ...theme.utils.fetchConfig('javascript'), body })
          .then((response) => response.json())
          .then(async (parsedState) => {
            if (parsedState.status) {
              this.handleErrorMessage(parsedState.description);
              document.dispatchEvent(new CustomEvent('ajaxProduct:error', {
                detail: {
                  errorMessage: parsedState.description
                }
              }));
              
              const submitButtonText = this.submitButton.querySelector('.btn-text span');
              if (!submitButtonText || !submitButtonText.hasAttribute('data-sold-out')) return;
              submitButtonText.innerText = submitButtonText.getAttribute('data-sold-out');
              this.submitButton.setAttribute('aria-disabled', 'true');
              this.error = true;
              return;
            }
    
            const cartJson = await (await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET')})).json();
            cartJson['sections'] = parsedState['sections'];
    
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'product-addon', cart: cartJson });
            document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
              detail: {
                product: parsedState
              }
            }));
    
            this.cartDrawer?.show(this.activeElement);
          })
          .catch((error) => {
            console.log(error);
          })
          .finally(() => {
            this.submitButton.removeAttribute('aria-busy');
    
            if (!this.error) {
              this.submitButton.removeAttribute('aria-disabled');
            }
          });
      }
    
      /**
       * Handles the display of an error message in a product form.
       *
       * @param {string|boolean} errorMessage - The error message to display. If false, the error message is hidden.
       * @return {void}
       */
      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;
        
        this.errorMessage = this.errorMessage || this.querySelector('.product-form__error-message');
        if (!this.errorMessage) return;
    
        this.errorMessage.toggleAttribute('hidden', !errorMessage);
        this.errorMessage.innerText = errorMessage;
      }
    }
  );
}
